import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays the product code (barcode) and MPN, updating
 * both as the shopper switches variants.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} display - The element that displays the code/MPN text
 * @property {HTMLScriptElement} data - JSON script tag mapping variant id -> { code, mpn }
 *
 * @extends {Component<Refs>}
 */
class ProductCodeMpnComponent extends Component {
  requiredRefs = ['display', 'data'];

  connectedCallback() {
    super.connectedCallback();

    try {
      this.variantDataMap = JSON.parse(this.refs.data.textContent || '{}');
    } catch {
      this.variantDataMap = {};
    }

    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    this.target = target;
    target.addEventListener(ThemeEvents.variantUpdate, this.updateDisplay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.target) return;
    this.target.removeEventListener(ThemeEvents.variantUpdate, this.updateDisplay);
  }

  /**
   * Updates the displayed code/MPN when the selected variant changes.
   * @param {CustomEvent} event - The variant update event.
   */
  updateDisplay = (event) => {
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;

    const data = this.variantDataMap[String(variantId)] || {};

    const parts = [];
    if (data.code) parts.push(`Product Code: ${data.code}`);
    if (data.mpn) parts.push(`MPN: ${data.mpn}`);
    if (data.sku) parts.push(`SKU: ${data.sku}`);

    if (parts.length) {
      this.refs.display.textContent = parts.join(' | ');
      this.style.display = 'block';
    } else {
      this.refs.display.textContent = '';
      this.style.display = 'none';
    }
  };
}

if (!customElements.get('product-code-mpn-component')) {
  customElements.define('product-code-mpn-component', ProductCodeMpnComponent);
}
